import { Editor, Text as SlateText, Element as SlateElement, Transforms, NodeEntry, BaseSelection } from "slate";
import { HEADING_TYPES, LIST_TYPES, SELECTION_PROPS, TEXT_ALIGN_TYPES, AlignKey, CustomText, EditorType, TextAction, ElementKey, TextFormatKey } from '../types';



export const getMarkSelectionProps = (editor: EditorType, id: TextFormatKey) => {
  if (isMarkActive(editor, id))
    return SELECTION_PROPS;
  return {};
};

/**
 * Some how this understands if text is modified with a format (say bold) and is currently selected - this is having an active mark of the right type
 * @param editor 
 * @param format 
 * @returns undefined -> false   or    true -> true
 */
export const isMarkActive = (editor: EditorType, format: TextFormatKey) => {
  return !!Editor.marks(editor)?.[format];
};

export const toggleMark = (editor: EditorType, format: TextFormatKey) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) editor.removeMark(format);
  else editor.addMark(format, true);
};

const isAlignFormat = (format: ElementKey) =>
  TEXT_ALIGN_TYPES.includes(format);


export const getBlockSelectionProps = (editor: EditorType, id: TextAction) => {
  if (isBlockActive(editor, id as ElementKey))
    return SELECTION_PROPS;
  return {};
};

/**
 * I think this is answering the question of whether the block is selected or not
 * 
 * path: is a way of refering to the location in a document. Each path is a simple array of numbers that refers to a node in the document tree 
 * by its indexes in each of its ancestor nodes down the tree.
 *   The first element in the document would have a path [0]
 *   The text in that element would have a path [0,0]
 * 
 * point: a point consists of a path and an offset.
 *    This is then a way of referring to the cursor in a selection
 *    The path is the location of the selection
 *    the offset is the cursor position in that selection
 * 
 * range: a range refers to a span of content between two points.
 * {
    "anchor": {
        "path": [
            2,
            0
        ],
        "offset": 0
    },
    "focus": {
        "path": [
            2,
            0
        ],
        "offset": 7
    }
 * The anchor and focus are set by the user. 
 *    The anchor is where the user began the selection and 
 *    the focus is where the user ends the selection
 * NB1 ranges always refer to leaf nodes in documents, never elements.
 * NB2 a collapsed range is one where the anchor and focus points refer to the exact same position in a document,
 *      signalling that there has been no selection
 *
 * selection: a selection (a special kind of Range) consistes of a Range and the Node[] encompassed by the selection
 * 
 * {
    "anchor": {
        "path": [
            2,
            0
        ],
        "offset": 0
    },
    "focus": {
        "path": [
            2,
            0
        ],
        "offset": 7
    },
    children: [
        {
          type: 'paragraph',
          children: [
            {
              text: 'A line of text!',
            },
          ],
        },
      ],
  }
 * 
 *
 * @param editor 
 * @param format 
 * @returns 
 */
export const isBlockActive = (editor: EditorType, format: ElementKey) => {
  const { selection } = editor;
  if (!selection) return false;

  const isAlign = isAlignFormat(format);
  const blockType = isAlign ? "align" : "type";

  const match = Array.from(
    Editor.nodes(editor, {
      // A range is hanging if it starts at the end of a node or ends at the start of a node.
      at: Editor.unhangRange(editor, selection),
      match: (node) => {
        return (
          !Editor.isEditor(node) &&
          SlateElement.isElement(node) &&
          node[blockType] === format
        );
      },
    })
  );

  return !!match?.[0];
};

export const isSelection = (editor: EditorType): BaseSelection | null => {
  const { selection } = editor;
  return selection;
}

export const isSelectionText = (editor: EditorType): NodeEntry<CustomText> | null => {
  const [textlike] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateText.isText(n),
  })
  return textlike as NodeEntry<CustomText> || false;
}

export const isSelectionBlock = (editor: EditorType): NodeEntry<CustomText> | null => {
  const [blocklike] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateElement.isElement(n),
  })
  return blocklike as NodeEntry<CustomText> || false;
}


export const toggleBlock = (editor: EditorType, format: ElementKey) => {
  const isAlign = TEXT_ALIGN_TYPES.includes(format);
  const isList = LIST_TYPES.includes(format);
  const isHeading = HEADING_TYPES.includes(format);
  const isActive = isBlockActive(editor, format);

  // check what form the selection takes
  console.log(isSelectionText(editor))
  console.log(isSelectionBlock(editor))

  let align: AlignKey | undefined;
  let type: string | undefined;

  if (isAlign) {
    align = isActive ? undefined : (format as AlignKey);
  } else if (isHeading) {
    type = isActive ? "paragraph" : format;
  } else if (isList) {
    // in the case of a list we're about to unwrap the paragraph
    type = isActive ? "paragraph" : format;
  }
  console.log(type)

  // removes the parent of the node
  Transforms.unwrapNodes(editor, {
    match: (node) => {
      console.log(SlateElement.isElement(node) && LIST_TYPES.includes(node.type as ElementKey))
      return (
        !Editor.isEditor(node) &&
        SlateElement.isElement(node) &&
        LIST_TYPES.includes(node.type as ElementKey) &&
        !HEADING_TYPES.includes(node.type as ElementKey) &&
        !isAlign
      );
    },
  });

  // in the case of lists the paragraph surrounding the selection will have been stripped un unwrapping, so the selection needs to retyped to
  // a list-item, before being wrapped in the list.
  // turns the selection into a list-item then creates a block out of the desired format (ul or ol in this case)
  // then wraps the 
  console.log(!isActive && isList)
  if (!isActive && isList) {
    type = "list-item";
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block, {split: true});
  }

  console.log(Editor.fragment(editor,[]))

  // align and list
  let newProperties: Partial<SlateElement> = {};
  if (isAlign) newProperties["align"] = align;
  if (type) newProperties["type"] = type;

  Transforms.setNodes<Editor>(editor, newProperties, {split: true});
};


export const findSelectedText = (editor: EditorType): string => {
  const { selection } = editor;
  if (!selection) return '';

  const [linkable] = Editor.nodes(editor, {
    match: n =>
      !Editor.isEditor(n) && SlateText.isText(n),
  })
  console.log(linkable);
  console.log(Editor.range(editor, selection))
  const textNode = linkable as NodeEntry<CustomText>
  const text = textNode[0].text;
  const end1: number = selection.anchor.offset
  const end2: number = selection.focus.offset
  const selectedText = text.substring(Math.min(end1,end2), Math.max(end1,end2));
  return selectedText;
}

/**
 * NB: the split:true provision is really, really important. If you don't specify that
 * Slate concludes that you want to wrap the entire slate content with the link!
 * @param editor 
 * @param url 
 * @param linkText 
 */
export const setLinkBlock = (editor: EditorType, url: string, linkText: string) => {
  const { selection } = editor
  if (!selection) return;
  
  Transforms.wrapNodes(
    editor,
    {
      type: 'link',
      url,
      children: [{ text: linkText }],
    },
    { at: Editor.range(editor, selection), split: true }
  )

  // this next thing might be useful, but not right now
  //Transforms.collapse(editor, { edge: 'end' })
}


